import { useState } from 'react';

import { useUsageApi, type UsageApiAdminDto } from '@/shared/api/hooksAdmin';
import { entier, horodatage, periodeCourante } from '@/shared/lib/utils';
import { Saisie } from '@/shared/ui/Champ';
import { Carte, EnTeteSection, Pastille, Table, Tuile, type ColonneTable } from '@/shared/ui/Table';

/**
 * Metering de l'API.
 *
 * LE SEUIL INCLUS VIENT DE L'API, il n'est pas code ici. Le §0.5 l'impose, et c'est
 * d'autant plus vrai que ce chiffre determine une facturation : un palier defini dans le
 * front divergerait tot ou tard de celui applique a la facture, et le client aurait raison
 * de contester celui qu'on lui a montre.
 *
 * Ce que le front calcule, en revanche, c'est le total de la colonne « facturables » —
 * une somme de valeurs deja fournies, pas une regle metier.
 */
export default function UsageApiPage() {
  const [periode, setPeriode] = useState(periodeCourante);
  const { data, isPending, isError, error, refetch } = useUsageApi(periode);

  const totalAppels = (data ?? []).reduce((somme, ligne) => somme + (ligne.nombreAppels ?? 0), 0);
  const totalFacturables = (data ?? []).reduce(
    (somme, ligne) => somme + (ligne.appelsFacturables ?? 0),
    0,
  );
  const enDepassement = (data ?? []).filter((ligne) => (ligne.appelsFacturables ?? 0) > 0).length;

  const colonnes: readonly ColonneTable<UsageApiAdminDto>[] = [
    {
      cle: 'entreprise',
      entete: 'Entreprise',
      rendu: (ligne) => <span className="font-medium text-texte">{ligne.raisonSociale}</span>,
    },
    {
      cle: 'appels',
      entete: 'Appels',
      numerique: true,
      rendu: (ligne) => entier(ligne.nombreAppels),
    },
    {
      cle: 'publique',
      entete: 'dont API publique',
      numerique: true,
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{entier(ligne.nombreAppelsApiPublique)}</span>,
    },
    {
      cle: 'seuil',
      entete: 'Seuil inclus',
      numerique: true,
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-faible">{entier(ligne.seuilInclus)}</span>,
    },
    {
      cle: 'facturables',
      entete: 'Facturables',
      numerique: true,
      rendu: (ligne) =>
        (ligne.appelsFacturables ?? 0) > 0 ? (
          <Pastille ton="alerte">{entier(ligne.appelsFacturables)}</Pastille>
        ) : (
          <span className="text-texte-faible">0</span>
        ),
    },
    {
      cle: 'dernier',
      entete: 'Dernier appel',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{horodatage(ligne.dernierAppelLe)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="Usage API"
        description="Volumes par entreprise, base de la facturation a l’usage."
        actions={
          <label className="flex items-center gap-2">
            <span className="text-texte-doux">Periode</span>
            <Saisie
              type="month"
              value={periode}
              onChange={(evenement) => setPeriode(evenement.target.value || periodeCourante())}
              className="w-36"
              aria-label="Periode"
            />
          </label>
        }
      />

      <section className="grid gap-3 sm:grid-cols-3">
        <Tuile libelle="Appels sur la periode" valeur={isPending ? '…' : entier(totalAppels)} />
        <Tuile
          libelle="Appels facturables"
          valeur={isPending ? '…' : entier(totalFacturables)}
          {...(totalFacturables > 0 ? { ton: 'alerte' as const } : {})}
        />
        <Tuile
          libelle="Entreprises en depassement"
          valeur={isPending ? '…' : entier(enDepassement)}
        />
      </section>

      <Table
        colonnes={colonnes}
        lignes={data}
        cleLigne={(ligne) => ligne.entrepriseId ?? ''}
        chargement={isPending}
        erreur={isError ? error : undefined}
        onReessayer={() => void refetch()}
        messageVide="Aucun appel enregistre sur cette periode."
      />

      <Carte className="p-3">
        <p className="text-texte-doux">
          Le seuil inclus est fourni par l’API, entreprise par entreprise. Il n’est pas
          reproduit dans ce front : un palier code ici finirait par diverger de celui applique
          a la facture.
        </p>
      </Carte>
    </div>
  );
}
